import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ADMINS_DIT_PATH } from '../../../../utils/constants';
import { AdminsPageShell } from '../../../../components/AdminsPageShell';
import { Autorenew as AutorenewIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { styles } from './styles';
import { useDitFileDownload } from '../../hooks/useDitFileDownload';
import { useDitGetEventDetails } from '../../hooks/useDitGetEventDetails';
import { useParams } from 'react-router-dom';
import { useSnackBarContext } from '../../../../contexts/SnackbarContext';
import { ResultsViewer } from './components/ResultsViewer';

const LoadingState = () => (
  <Box sx={styles.loadingStateContainer}>
    <Skeleton variant="text" width="40%" height={30} />
    <Skeleton variant="text" width="60%" height={30} />
    <Skeleton variant="rectangular" sx={styles.loadingSkeleton} />
    <Skeleton variant="rectangular" sx={styles.loadingSkeleton} />
    <Skeleton variant="rectangular" sx={styles.loadingSkeleton} />
  </Box>
);

const ErrorState = ({ error }) => (
  <Box sx={styles.errorStateContainer}>
    <Typography variant="h6" sx={styles.errorStateTitle}>
      Error
    </Typography>
    <Typography variant="body1">{error.message}</Typography>
    <Button variant="outlined" color="error" onClick={() => window.location.reload()}>
      Retry
    </Button>
  </Box>
);

const ProgressBar = ({ value, status }) => {
  value = value || 0;
  let progressColor;
  if (value >= 100) {
    progressColor = '#4caf50';
  } else if (status === 'FAILED') {
    progressColor = '#f44336';
  } else {
    progressColor = '#2196f3';
  }

  return (
    <Box sx={styles.progressBarContainer}>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          ...styles.progressBar,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: progressColor,
          },
        }}
      />
      <Typography variant="body2" sx={styles.progressBarText}>
        {`${value.toFixed(2)}%`}
      </Typography>
    </Box>
  );
};

export const DitEventDetails = () => {
  const { id } = useParams();
  const { setSnack } = useSnackBarContext();
  const [downloadingSource, setDownloadingSource] = useState(false);
  const [downloadingResults, setDownloadingResults] = useState(false);
  const { eventDetails, loading, error, refetch } = useDitGetEventDetails(id);
  const isRunning = useMemo(() => {
    const status = eventDetails?.status?.toLowerCase() || '';
    const message = eventDetails?.message?.toLowerCase() || '';
    return status === 'running' || message.includes('generating results');
  }, [eventDetails]);

  const [resultsBuffer, setResultsBuffer] = useState(null);
  const [resultsError, setResultsError] = useState(null);
  const lastFetchedResultsKeyRef = useRef(null);

  const { handleFileDownload } = useDitFileDownload();

  const handleFileDownloadClick = async (fileDetails, filename, setDownloading) => {
    setDownloading(true);

    try {
      await handleFileDownload(fileDetails, filename);
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to download file: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleRefresh = async () => {
    await refetch();
  };

  // Automatically refresh every 10 seconds if the event is running
  useEffect(() => {
    if (!loading && isRunning) {
      const interval = setInterval(() => {
        refetch();
      }, 10000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [isRunning, loading, refetch]);

  // download results file when event details are loaded to display in ResultsViewer
  useEffect(() => {
    const key = eventDetails?.results?.s3FileKey;
    if (!key) return;

    // prevent repeated downloads of the same key
    if (lastFetchedResultsKeyRef.current === key) return;
    lastFetchedResultsKeyRef.current = key;

    const filename = key.split('/').pop();
    handleFileDownload(eventDetails.results, filename, setResultsBuffer).catch((error) => {
      setResultsError(error);
      setSnack({
        open: true,
        message: `Failed to download results: ${error.message}`,
        severity: 'error',
      });
    });
  }, [eventDetails?.results, handleFileDownload, setSnack]);

  return (
    <AdminsPageShell>
      <Box sx={styles.header}>
        <Typography variant="h5" gutterBottom sx={styles.title}>
          Data Ingestion Tool - Event Details
          {isRunning && <AutorenewIcon sx={styles.refreshIcon} />}
        </Typography>
        <Box sx={styles.headerActions}>
          <Button
            variant="text"
            color="secondary"
            href={ADMINS_DIT_PATH}
            startIcon={<ArrowBackIcon />}
            sx={{ alignSelf: 'start' }}
          >
            <Typography variant="body1" sx={{ color: 'secondary' }}>
              Back to Dashboard
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRefresh}
            disabled={loading || !isRunning}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {loading && !eventDetails && <LoadingState />}
      {error && <ErrorState error={error} />}

      {eventDetails && (
        <Grid2 container spacing={2} direction="column">
          <Grid2 item xs={12}>
            <Typography sx={styles.label}>
              <strong>ID:</strong> {eventDetails.id}
            </Typography>
          </Grid2>
          <Grid2 item xs={12}>
            <Typography sx={styles.label}>
              <strong>Source:</strong> {eventDetails.source?.name}
            </Typography>
          </Grid2>
          <Grid2 item xs={12}>
            <Typography sx={styles.label}>
              <strong>User:</strong> {eventDetails.username}
            </Typography>
          </Grid2>
          {eventDetails.sourceFiles?.length > 0 && (
            <Grid2 item xs={12}>
              <Box sx={styles.downloadFileBox}>
                <Typography sx={styles.label}>
                  <strong>Source Files:</strong>
                </Typography>
                <Box sx={styles.sourceFilesContainer}>
                  {eventDetails.sourceFiles.map((file, index) => (
                    <Button
                      key={index}
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        handleFileDownloadClick(file, file.filename, setDownloadingSource)
                      }
                      disabled={downloadingSource}
                      sx={styles.downloadSourceFileButton}
                    >
                      {file.filename}
                    </Button>
                  ))}
                </Box>
                {downloadingSource && <CircularProgress size={24} style={styles.spinner} />}
              </Box>
            </Grid2>
          )}
          <Grid2 item xs={12}>
            <Typography
              sx={{
                ...styles.label,
                color: eventDetails.status === 'FAILED' ? '#f44336' : 'inherit',
              }}
            >
              <strong>Status:</strong> {eventDetails.status}
            </Typography>
          </Grid2>
          <Grid2 item xs={12}>
            <Typography
              sx={{
                ...styles.messageLabel,
                color: eventDetails.status === 'FAILED' ? '#f44336' : 'inherit',
              }}
            >
              <strong>Message:</strong> {eventDetails.message}
            </Typography>
          </Grid2>
          <Grid2 item xs={12}>
            <Box sx={styles.progressContainer}>
              <Typography sx={styles.label}>
                <strong>File Normalization Progress:</strong>
              </Typography>
              <ProgressBar
                value={eventDetails.fileNormalizationProgress}
                status={eventDetails.status}
              />
            </Box>
          </Grid2>
          <Grid2 item xs={12}>
            <Box sx={styles.progressContainer}>
              <Typography sx={styles.label}>
                <strong>Ingestion Pipeline Progress:</strong>
              </Typography>
              <ProgressBar value={eventDetails.ingestionProgress} status={eventDetails.status} />
            </Box>
          </Grid2>
          <Grid2 item xs={12}>
            <Typography sx={styles.label}>
              <strong>Start Time:</strong> {new Date(eventDetails.timestamp).toLocaleString()}
            </Typography>
          </Grid2>
          <Grid2 item xs={12}>
            <Typography sx={styles.label}>
              <strong>Last Updated:</strong> {new Date(eventDetails.updatedAt).toLocaleString()}
            </Typography>
          </Grid2>
          {eventDetails.results?.s3FileKey && (
            <Grid2 item xs={12}>
              <Box sx={styles.downloadFileBox}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    handleFileDownloadClick(
                      eventDetails.results,
                      eventDetails.results.s3FileKey.split('/').pop(),
                      setDownloadingResults,
                    )
                  }
                  disabled={downloadingResults}
                >
                  Download Results
                </Button>
                {downloadingResults && <CircularProgress size={24} style={styles.spinner} />}
              </Box>
            </Grid2>
          )}
        </Grid2>
      )}
      {eventDetails?.results && !resultsError && <ResultsViewer fileBuffer={resultsBuffer} />}
    </AdminsPageShell>
  );
};
